// ── Lacuna: main entry point and router ──

import { initFirebase, signInAnon, getCurrentUid } from './firebase';
import { renderLobby, renderWaiting } from './ui/lobby';
import { renderHUD } from './ui/hud';
import { renderTurnPanel, renderPieRule } from './ui/turnPanel';
import { renderGameOver } from './ui/gameOver';
import { renderRules } from './ui/rules';
import { isSpectator, renderJoinPrompt } from './ui/spectator';
import { createBoardSVG, renderBoard } from './board/renderer';
import {
  beginNodePlacement,
  handleBoardPointerDown,
  handleBoardPointerMove,
  handleBoardPointerUp,
  cancelInteraction,
  undoLastStrand,
  undoNodePlacement,
  getInteractionState,
} from './board/interaction';
import { createGame } from './sync/host';
import { joinGame, subscribeToGame, lookupRoomCode } from './sync/client';
import { writeTensionChoice, writeKChoice, writeMove, writePieSwap } from './sync/moves';
import { drawPair } from './game/bag';
import type { GameState, PlayerSlot } from './game/state';
import { getPlayerSlotByUid, getActiveColor } from './game/state';
import './style.css';

// ── State ──

let currentGameId: string | null = null;
let currentState: GameState | null = null;
let mySlot: PlayerSlot | null = null;
let boardSvg: SVGSVGElement | null = null;
let unsubscribe: (() => void) | null = null;

const app = document.getElementById('app')!;

// ── Init ──

async function init(): Promise<void> {
  initFirebase();
  await signInAnon();
  route();
  window.addEventListener('hashchange', route);
}

// ── Router ──

function route(): void {
  const hash = window.location.hash;

  // Clean up previous subscription
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  cancelInteraction();

  if (hash.startsWith('#/g/')) {
    const gameId = hash.slice(4);
    enterGame(gameId);
  } else if (hash === '#/rules') {
    showRules();
  } else {
    showLobby();
  }
}

// ── Lobby ──

function showLobby(): void {
  app.innerHTML = '';
  renderLobby(app, {
    onCreateGame: async (name) => {
      try {
        const { gameId, roomCode } = await createGame(name);
        currentGameId = gameId;
        window.location.hash = `#/g/${gameId}`;
      } catch (err) {
        console.error('Failed to create game:', err);
        alert('Failed to create game. Check console for details.');
      }
    },
    onJoinGame: async (code, name) => {
      try {
        const gameId = await lookupRoomCode(code);
        if (!gameId) {
          alert('Room not found');
          return;
        }
        await joinGame(gameId, name);
        currentGameId = gameId;
        window.location.hash = `#/g/${gameId}`;
      } catch (err) {
        console.error('Failed to join game:', err);
        alert('Failed to join game. It may be full or already started.');
      }
    },
    onViewRules: () => {
      window.location.hash = '#/rules';
    },
  });
}

// ── Rules ──

function showRules(): void {
  app.innerHTML = '';
  renderRules(app, () => {
    window.location.hash = '';
  });
}

// ── Game ──

function enterGame(gameId: string): void {
  currentGameId = gameId;
  app.innerHTML = '';

  // Create the game layout
  const gameContainer = document.createElement('div');
  gameContainer.className = 'game-container';

  const boardContainer = document.createElement('div');
  boardContainer.className = 'board-container';

  const panelContainer = document.createElement('div');
  panelContainer.className = 'panel-container';

  gameContainer.appendChild(boardContainer);
  gameContainer.appendChild(panelContainer);
  app.appendChild(gameContainer);

  // Create SVG board
  boardSvg = createBoardSVG();
  boardContainer.appendChild(boardSvg);

  // Disable browser touch gestures (scroll, zoom, long-press) on the board
  boardSvg.style.touchAction = 'none';

  // Board pointer handlers — pointerdown places nodes or starts strand drag
  boardSvg.addEventListener('pointerdown', (e) => {
    if (!boardSvg || !currentState || !mySlot) return;
    if (currentState.turn.activePlayer !== mySlot) return;
    e.preventDefault();
    // Capture so pointermove/pointerup fire even if finger leaves the SVG
    boardSvg.setPointerCapture(e.pointerId);
    handleBoardPointerDown(boardSvg, currentState.board, e.clientX, e.clientY);
  });

  // pointermove — drives node preview and strand drag sampling
  boardSvg.addEventListener('pointermove', (e) => {
    if (!boardSvg) return;
    e.preventDefault();
    handleBoardPointerMove(boardSvg, e.clientX, e.clientY);
  });

  // pointerup — finishes strand drag near a valid target
  boardSvg.addEventListener('pointerup', (e) => {
    if (!boardSvg || !currentState || !mySlot) return;
    if (currentState.turn.activePlayer !== mySlot) return;
    e.preventDefault();
    handleBoardPointerUp(boardSvg, currentState.board, e.clientX, e.clientY);
  });

  // Subscribe to game state
  unsubscribe = subscribeToGame(gameId, (state) => {
    if (!state) {
      app.innerHTML = '<div class="lobby"><p>Game not found.</p></div>';
      return;
    }
    onStateUpdate(state, gameContainer, boardContainer, panelContainer);
  });
}

function onStateUpdate(
  state: GameState,
  gameContainer: HTMLElement,
  boardContainer: HTMLElement,
  panelContainer: HTMLElement,
): void {
  const prevState = currentState;
  currentState = state;

  // Determine my role
  const uid = getCurrentUid();
  mySlot = uid ? getPlayerSlotByUid(state, uid) : null;

  // Waiting for player 2
  if (state.meta.status === 'waiting') {
    if (mySlot === 'player1') {
      // Show waiting screen inside the game container (don't destroy it)
      boardContainer.style.display = 'none';
      panelContainer.innerHTML = '';
      // Only render the waiting card once
      if (!gameContainer.querySelector('.waiting-card')) {
        const waitingDiv = document.createElement('div');
        waitingDiv.className = 'lobby waiting';
        waitingDiv.style.flex = '1';
        const shareUrl = `${window.location.origin}${window.location.pathname}#/g/${currentGameId}`;
        waitingDiv.innerHTML = `
          <div class="lobby-header">
            <h1 class="lobby-title">LACUNA</h1>
            <p class="lobby-subtitle">Waiting for opponent&hellip;</p>
          </div>
          <div class="waiting-card">
            <div class="room-code-label">Room Code</div>
            <div class="room-code">${state.meta.roomCode}</div>
            <div class="share-link">
              <input type="text" readonly value="${shareUrl}" id="share-url" />
              <button class="btn btn-small" id="btn-copy">Copy</button>
            </div>
          </div>
          <div class="waiting-spinner"></div>
        `;
        gameContainer.insertBefore(waitingDiv, boardContainer);

        const copyBtn = waitingDiv.querySelector('#btn-copy') as HTMLButtonElement;
        const shareInput = waitingDiv.querySelector('#share-url') as HTMLInputElement;
        copyBtn.addEventListener('click', () => {
          navigator.clipboard.writeText(shareUrl).then(() => {
            copyBtn.textContent = 'Copied!';
            setTimeout(() => (copyBtn.textContent = 'Copy'), 2000);
          });
        });
        shareInput.addEventListener('click', () => shareInput.select());
      }
      return;
    }
    // Spectator can join
    if (!mySlot) {
      renderJoinPrompt(panelContainer, state, currentGameId!, {
        onJoinAsPlayer: async (gid, name) => {
          try {
            await joinGame(gid, name);
          } catch (err) {
            console.error('Failed to join:', err);
          }
        },
      });
    }
    return;
  }

  // Transition from waiting → playing: remove waiting screen, show board
  const waitingEl = gameContainer.querySelector('.waiting');
  if (waitingEl) {
    waitingEl.remove();
    boardContainer.style.display = '';
  }

  // Render board
  if (boardSvg) {
    renderBoard(boardSvg, state);
  }

  // Render HUD
  renderHUD(gameContainer, state, mySlot);

  // Game over
  if (state.meta.status === 'finished') {
    renderGameOver(gameContainer, state, mySlot);
    renderTurnPanel(panelContainer, state, mySlot, dummyCallbacks());
    return;
  }

  // Pie rule check: after turn 1, before player 2's first move
  if (
    state.meta.pieRuleAvailable &&
    state.turn.number === 2 &&
    mySlot === 'player2' &&
    state.turn.phase === 'chooseTension'
  ) {
    renderPieRule(panelContainer, {
      onSwap: () => writePieSwap(currentGameId!, state, true),
      onDecline: () => writePieSwap(currentGameId!, state, false),
    });
    return;
  }

  // Turn panel
  const interactionInfo = getInteractionState();
  renderTurnPanel(panelContainer, state, mySlot, {
    onChooseTension: async (token) => {
      try {
        await writeTensionChoice(currentGameId!, state, token);
      } catch (err) {
        console.error('Failed to write tension:', err);
      }
    },
    onChooseK: async (k) => {
      try {
        await writeKChoice(currentGameId!, k);
        // Start node placement interaction
        if (boardSvg && currentState) {
          const color = getActiveColor(currentState);
          beginNodePlacement(boardSvg, currentState.board, k, color, async (move) => {
            try {
              await writeMove(
                currentGameId!, currentState!,
                move.nodeX, move.nodeY,
                move.strandPaths.map(s => s.targetId),
                move.strandPaths.map(s => s.waypoints),
              );
            } catch (err) {
              console.error('Failed to write move:', err);
            }
          }, () => {
            // Re-render turn panel on interaction state change
            const info = getInteractionState();
            renderTurnPanel(panelContainer, currentState!, mySlot, makeUndoCallbacks(panelContainer), info);
          });
        }
      } catch (err) {
        console.error('Failed to write k:', err);
      }
    },
    onConfirmMove: () => {},
    onUndoStrand: () => {
      undoLastStrand();
      const info = getInteractionState();
      renderTurnPanel(panelContainer, currentState!, mySlot, makeUndoCallbacks(panelContainer), info);
    },
    onUndoNode: () => {
      undoNodePlacement();
      const info = getInteractionState();
      renderTurnPanel(panelContainer, currentState!, mySlot, makeUndoCallbacks(panelContainer), info);
    },
  }, interactionInfo);

  // If we're in placeNode/drawStrands phase and interaction isn't started, start it
  if (
    mySlot &&
    state.turn.activePlayer === mySlot &&
    (state.turn.phase === 'placeNode' || state.turn.phase === 'drawStrands') &&
    state.turn.chosenK &&
    boardSvg &&
    interactionInfo.phase === 'idle'
  ) {
    const color = getActiveColor(state);
    beginNodePlacement(boardSvg, state.board, state.turn.chosenK, color, async (move) => {
      try {
        await writeMove(
          currentGameId!, state,
          move.nodeX, move.nodeY,
          move.strandPaths.map(s => s.targetId),
          move.strandPaths.map(s => s.waypoints),
        );
      } catch (err) {
        console.error('Failed to write move:', err);
      }
    }, () => {
      const info = getInteractionState();
      renderTurnPanel(panelContainer, currentState!, mySlot, makeUndoCallbacks(panelContainer), info);
    });
  }
}

function makeUndoCallbacks(panelContainer: HTMLElement) {
  const rerender = () => {
    const info = getInteractionState();
    renderTurnPanel(panelContainer, currentState!, mySlot, makeUndoCallbacks(panelContainer), info);
  };
  return {
    onChooseTension: () => {},
    onChooseK: () => {},
    onConfirmMove: () => {},
    onUndoStrand: () => {
      undoLastStrand();
      rerender();
    },
    onUndoNode: () => {
      undoNodePlacement();
      rerender();
    },
  };
}

function dummyCallbacks() {
  return {
    onChooseTension: () => {},
    onChooseK: () => {},
    onConfirmMove: () => {},
    onUndoStrand: () => {},
    onUndoNode: () => {},
  };
}

// ── Start ──

init().catch(console.error);
